import type { Command } from "@/commands/Command";
import { ReplaceNodesCommand } from "@/commands/nodeCommands";
import {
  AddVectorVertexCommand,
  BendVectorEdgeCommand,
  ConnectVectorVerticesCommand,
  DeleteVectorEdgesCommand,
  DeleteVectorVertexCommand,
  InsertVectorVertexAtIntersectionCommand,
  InsertVectorVertexOnEdgeCommand,
  KnifeCutAroundVertexCommand,
  KnifeCutVectorEdgesCommand,
  MergeVectorVerticesCommand,
  MoveVectorHandlesCommand,
  MoveVectorVertexCommand,
  ExtrudeVectorEdgesCommand,
  SubdivideVectorEdgesCommand,
  ToggleVectorRegionFillCommand,
  UpdateVectorVertexSettingsCommand,
} from "@/commands/vectorCommands";
import { getAllSceneNodes, type CurvaDocument } from "@/document/model/document";
import type { VectorNetworkNode } from "@/document/model/nodes";
import type { VectorNetwork } from "@/document/model/vectorNetwork";
import { getVectorNetworkBounds } from "@/document/operations/vectorGeometry";
import { findVertexSnapTarget } from "@/document/operations/vectorVertexSnap";
import { vectorHandleUpdatesForModeChange, vectorHandleUpdatesForMove } from "@/document/operations/vectorHandleMirroring";
import { splitVectorNetworkToRegions } from "@/document/operations/vectorSplitRegions";
import { selectionStore, documentStore } from "@/editor/store";
import { createId } from "@/lib/ids";
import { transformRotationOrigin } from "@/lib/math/rotation";
import { TransactionCommand } from "@/commands/transaction";
import type { EditorCommandHost } from "../editorCommandHost";

type Point = { x: number; y: number };
type VertexPosition = { vertexId: string } & Point;
type HandlePosition = { edgeId: string; endpoint: "from" | "to" } & Point;

function vectorNode(nodeId: string, document: CurvaDocument = documentStore.getDocument()): VectorNetworkNode | undefined {
  const node = getAllSceneNodes(document).find(candidate => candidate.id === nodeId);
  return node?.type === "vectorNetwork" ? node : undefined;
}

function hasEdge(node: VectorNetworkNode, fromVertexId: string, toVertexId: string): boolean {
  return node.network.edges.some(edge => edge.from.vertexId === fromVertexId && edge.to.vertexId === toVertexId);
}

function moveHandlesCommandForPositions(
  node: VectorNetworkNode,
  nodeId: string,
  handlePositions: HandlePosition[] | undefined,
  vertexPositions: VertexPosition[],
): Command | undefined {
  if (!handlePositions?.length) {
    return undefined;
  }

  const vertexDeltaById = new Map(
    vertexPositions.flatMap(position => {
      const vertex = node.network.vertices.find(candidate => candidate.id === position.vertexId);
      return vertex ? [[position.vertexId, { x: position.x - vertex.x, y: position.y - vertex.y }] as const] : [];
    }),
  );
  const previous = handlePositions.flatMap(position => {
    const edge = node.network.edges.find(candidate => candidate.id === position.edgeId);
    const handle = edge?.[position.endpoint].handle;
    if (!edge || !handle) {
      return [];
    }

    const endpointVertexId = edge[position.endpoint].vertexId;
    const delta = vertexDeltaById.get(endpointVertexId) ?? { x: 0, y: 0 };
    return [{
      edgeId: position.edgeId,
      endpoint: position.endpoint,
      handle: { x: handle.x + delta.x, y: handle.y + delta.y },
    }];
  });
  if (previous.length === 0) {
    return undefined;
  }

  return new MoveVectorHandlesCommand(
    nodeId,
    previous,
    handlePositions.map(position => ({
      edgeId: position.edgeId,
      endpoint: position.endpoint,
      handle: { x: position.x, y: position.y },
    })),
  );
}

export function moveVectorVertex(
  host: EditorCommandHost,
  nodeId: string,
  vertexId: string,
  x: number,
  y: number,
  vertexPositions?: VertexPosition[],
  handlePositions?: HandlePosition[],
): void {
  const node = vectorNode(nodeId);
  if (!node) return;

  const positions = vertexPositions?.length ? vertexPositions : [{ vertexId, x, y }];
  const movedVertexIds = positions.map(position => position.vertexId);
  selectionStore.updateFreshCutVertexIds(current => current.filter(id => !movedVertexIds.includes(id)));
  if (positions.length > 1) {
    const commands: Command[] = positions.flatMap(position => {
      const vertex = node.network.vertices.find(candidate => candidate.id === position.vertexId);
      return vertex && (vertex.x !== position.x || vertex.y !== position.y)
        ? [new MoveVectorVertexCommand(nodeId, position.vertexId, { x: vertex.x, y: vertex.y }, { x: position.x, y: position.y })]
        : [];
    });
    const handleCommand = moveHandlesCommandForPositions(node, nodeId, handlePositions, positions);
    if (handleCommand) commands.push(handleCommand);
    if (commands.length === 0) return;

    const nextSelection = host.selectionSnapshot({
      selectedVertexId: vertexId, selectedVertexIds: movedVertexIds, selectedEdgeIds: [], selectedHandle: undefined,
    });
    const context = host.commandContext();
    host.history.execute(commands.length === 1 ? commands[0]! : new TransactionCommand("Moved vertices", commands), context, { afterSelection: nextSelection });
    host.applySelectionSnapshot(nextSelection);
    host.commit(context.document, "Moved vertices");
    return;
  }

  const vertex = node.network.vertices.find(candidate => candidate.id === vertexId);
  if (!vertex) return;

  const snapTarget = findVertexSnapTarget(node.network.vertices, vertexId, { x, y });
  if (snapTarget) {
    const context = host.commandContext();
    const nextSelection = host.selectionSnapshot({ selectedVertexId: snapTarget.id, selectedEdgeIds: [], selectedHandle: undefined });
    host.history.execute(new MergeVectorVerticesCommand(nodeId, vertexId, snapTarget.id, {
      from: { x: vertex.x, y: vertex.y }, to: { x: snapTarget.x, y: snapTarget.y },
    }), context, { afterSelection: nextSelection });
    host.applySelectionSnapshot(nextSelection);
    host.commit(context.document, "Merged vertices");
    return;
  }

  const handleCommand = moveHandlesCommandForPositions(node, nodeId, handlePositions, [{ vertexId, x, y }]);
  if (vertex.x === x && vertex.y === y) {
    if (!handleCommand) return;
    const nextSelection = host.selectionSnapshot({ selectedVertexId: vertexId, selectedEdgeIds: [], selectedHandle: undefined });
    const context = host.commandContext();
    host.history.execute(handleCommand, context, { afterSelection: nextSelection });
    host.applySelectionSnapshot(nextSelection);
    host.commit(context.document, "Moved vertex");
    return;
  }

  const commands: Command[] = [new MoveVectorVertexCommand(nodeId, vertexId, { x: vertex.x, y: vertex.y }, { x, y })];
  if (handleCommand) commands.push(handleCommand);
  const nextSelection = host.selectionSnapshot({ selectedVertexId: vertexId, selectedEdgeIds: [], selectedHandle: undefined });
  const context = host.commandContext();
  host.history.execute(commands.length === 1 ? commands[0]! : new TransactionCommand("Moved vertex", commands), context, { afterSelection: nextSelection });
  host.applySelectionSnapshot(nextSelection);
  host.commit(context.document, "Moved vertex");
}

export function moveVectorEdgeVertices(host: EditorCommandHost, nodeId: string, edgeIds: string[], positions: VertexPosition[]): void {
  const node = vectorNode(nodeId);
  if (!node || positions.length === 0) return;
  selectionStore.updateFreshCutVertexIds(current => current.filter(id => !positions.some(position => position.vertexId === id)));
  const commands = positions.flatMap(position => {
    const vertex = node.network.vertices.find(candidate => candidate.id === position.vertexId);
    return vertex && (vertex.x !== position.x || vertex.y !== position.y)
      ? [new MoveVectorVertexCommand(nodeId, position.vertexId, { x: vertex.x, y: vertex.y }, { x: position.x, y: position.y })]
      : [];
  });
  if (commands.length === 0) return;

  const nextSelection = host.selectionSnapshot({
    selectedVertexId: undefined, selectedVertexIds: [], selectedEdgeIds: edgeIds, selectedHandle: undefined,
  });
  const context = host.commandContext();
  host.history.execute(commands.length === 1 ? commands[0]! : new TransactionCommand("Moved edges", commands), context, { afterSelection: nextSelection });
  host.applySelectionSnapshot(nextSelection);
  host.commit(context.document, edgeIds.length > 1 ? "Moved edges" : "Moved edge");
}

export function sculptVectorVertices(host: EditorCommandHost, nodeId: string, positions: VertexPosition[]): void {
  const node = vectorNode(nodeId);
  if (!node || positions.length === 0) return;
  const commands = positions.flatMap(position => {
    const vertex = node.network.vertices.find(candidate => candidate.id === position.vertexId);
    return vertex && (vertex.x !== position.x || vertex.y !== position.y)
      ? [new MoveVectorVertexCommand(nodeId, position.vertexId, { x: vertex.x, y: vertex.y }, { x: position.x, y: position.y })]
      : [];
  });
  if (commands.length === 0) return;

  const context = host.commandContext();
  host.history.execute(
    commands.length === 1 ? commands[0]! : new TransactionCommand("Sculpted vertices", commands),
    context,
    { afterSelection: host.currentSelection() },
  );
  host.commit(context.document, "Sculpted vertices");
}

export function moveVectorHandle(host: EditorCommandHost, nodeId: string, edgeId: string, endpoint: "from" | "to", x: number, y: number): void {
  const node = vectorNode(nodeId);
  const edge = node?.network.edges.find(candidate => candidate.id === edgeId);
  const handle = edge?.[endpoint].handle;
  if (!node || !edge || !handle || (handle.x === x && handle.y === y)) return;

  const updates = vectorHandleUpdatesForMove(node.network, { edgeId, endpoint }, { x, y });
  const previous = updates.map(update => {
    const updateHandle = node.network.edges.find(candidate => candidate.id === update.edgeId)?.[update.endpoint].handle;
    return { edgeId: update.edgeId, endpoint: update.endpoint, handle: updateHandle ? { x: updateHandle.x, y: updateHandle.y } : null };
  });
  if (previous.length === 0) return;
  const next = updates.map(update => ({ edgeId: update.edgeId, endpoint: update.endpoint, handle: { x: update.x, y: update.y } }));
  const nextSelection = host.selectionSnapshot({ selectedHandle: { edgeId, endpoint }, selectedVertexId: undefined, selectedEdgeIds: [] });
  const context = host.commandContext();
  host.history.execute(new MoveVectorHandlesCommand(nodeId, previous, next), context, { afterSelection: nextSelection });
  host.applySelectionSnapshot(nextSelection);
  host.commit(context.document, "Moved handle");
}

export function insertVectorVertexOnEdge(host: EditorCommandHost, nodeId: string, edgeId: string, point: Point, t: number): void {
  const insertedVertexId = createId("vertex");
  const context = host.commandContext();
  const nextSelection = host.selectionSnapshot({
    selectedNodeIds: [nodeId], primarySelectedNodeId: nodeId, mode: "vectorEdit", selectedVertexId: insertedVertexId, selectedEdgeIds: [], selectedHandle: undefined,
  });
  host.history.execute(new InsertVectorVertexOnEdgeCommand(nodeId, edgeId, point, t, { vertexId: insertedVertexId }), context, { afterSelection: nextSelection });
  host.applySelectionSnapshot(nextSelection);
  host.commit(context.document, "Inserted vertex");
}

export function insertVectorVertexAtIntersection(
  host: EditorCommandHost, nodeId: string, point: Point, edges: [{ edgeId: string; t: number }, { edgeId: string; t: number }],
): void {
  const insertedVertexId = createId("vertex");
  const context = host.commandContext();
  const nextSelection = host.selectionSnapshot({
    selectedNodeIds: [nodeId], primarySelectedNodeId: nodeId, mode: "vectorEdit", selectedVertexId: insertedVertexId, selectedEdgeIds: [], selectedHandle: undefined,
  });
  host.history.execute(new InsertVectorVertexAtIntersectionCommand(nodeId, point, edges[0], edges[1], { vertexId: insertedVertexId }), context, { afterSelection: nextSelection });
  host.applySelectionSnapshot(nextSelection);
  host.commit(context.document, "Inserted intersection");
}

export function connectPenToInsertedVertex(
  host: EditorCommandHost, nodeId: string, fromVertexId: string, edgeId: string, point: Point, t: number,
): void {
  const node = vectorNode(nodeId);
  const edge = node?.network.edges.find(candidate => candidate.id === edgeId);
  if (!node) return;
  if (!edge || edge.from.vertexId === fromVertexId || edge.to.vertexId === fromVertexId) {
    insertVectorVertexOnEdge(host, nodeId, edgeId, point, t);
    return;
  }
  const insertedVertexId = createId("vertex");
  const nextSelection = host.selectionSnapshot({
    selectedNodeIds: [nodeId], primarySelectedNodeId: nodeId, mode: "vectorEdit", selectedVertexId: insertedVertexId, selectedEdgeIds: [], selectedHandle: undefined,
  });
  const context = host.commandContext();
  host.history.execute(new TransactionCommand("Connect to inserted vertex", [
    new InsertVectorVertexOnEdgeCommand(nodeId, edgeId, point, t, { vertexId: insertedVertexId, edgeIds: [createId("edge"), createId("edge")] }),
    new ConnectVectorVerticesCommand(nodeId, createId("edge"), fromVertexId, insertedVertexId),
  ]), context, { afterSelection: nextSelection });
  host.applySelectionSnapshot(nextSelection);
  host.commit(context.document, "Connected to edge");
}

export function addPenVertex(host: EditorCommandHost, nodeId: string, fromVertexId: string | undefined, point: Point): { vertexId: string; edgeId?: string } | undefined {
  if (!vectorNode(nodeId)) return undefined;
  const vertexId = createId("vertex");
  const edgeId = fromVertexId ? createId("edge") : undefined;
  const nextSelection = host.selectionSnapshot({
    selectedNodeIds: [nodeId], primarySelectedNodeId: nodeId, mode: "vectorEdit", selectedVertexId: vertexId, selectedEdgeIds: [], selectedHandle: undefined,
  });
  const context = host.commandContext();
  host.history.execute(new AddVectorVertexCommand(nodeId, { id: vertexId, x: point.x, y: point.y, handleMode: "mirrorAngleAndLength" }, edgeId && fromVertexId ? { id: edgeId, fromVertexId } : undefined), context, { afterSelection: nextSelection });
  host.applySelectionSnapshot(nextSelection);
  host.commit(context.document, "Added point");
  return { vertexId, edgeId };
}

export function connectPenVertices(host: EditorCommandHost, nodeId: string, fromVertexId: string, toVertexId: string): { edgeId: string } | undefined {
  if (fromVertexId === toVertexId) return undefined;
  const node = vectorNode(nodeId);
  if (!node || hasEdge(node, fromVertexId, toVertexId)) return undefined;
  const edgeId = createId("edge");
  const nextSelection = host.selectionSnapshot({
    selectedNodeIds: [nodeId], primarySelectedNodeId: nodeId, mode: "vectorEdit", selectedVertexId: toVertexId, selectedEdgeIds: [], selectedHandle: undefined,
  });
  const context = host.commandContext();
  host.history.execute(new ConnectVectorVerticesCommand(nodeId, edgeId, fromVertexId, toVertexId), context, { afterSelection: nextSelection });
  host.applySelectionSnapshot(nextSelection);
  host.commit(context.document, "Connected points");
  return { edgeId };
}

export function deleteSelectedVectorSelection(host: EditorCommandHost, options: { reconnectVertices?: boolean } = {}): boolean {
  const selection = host.currentSelection();
  const selectedEdgeIds = selection.selectedEdgeIds ?? [];
  const selectedVertexIds = selection.selectedVertexIds ?? [];
  if (selection.mode !== "vectorEdit" || !selection.primarySelectedNodeId) return false;
  const context = host.commandContext();
  if (selection.selectedHandle) {
    const node = vectorNode(selection.primarySelectedNodeId, context.document);
    const edge = node?.network.edges.find(candidate => candidate.id === selection.selectedHandle!.edgeId);
    const current = edge?.[selection.selectedHandle.endpoint].handle;
    if (!edge || !current) return false;
    const nextSelection = host.selectionSnapshot({ selectedHandle: undefined });
    host.history.execute(new MoveVectorHandlesCommand(selection.primarySelectedNodeId, [{
      edgeId: selection.selectedHandle.edgeId, endpoint: selection.selectedHandle.endpoint, handle: { x: current.x, y: current.y },
    }], [{
      edgeId: selection.selectedHandle.edgeId, endpoint: selection.selectedHandle.endpoint, handle: null,
    }]), context, { afterSelection: nextSelection });
    host.applySelectionSnapshot(nextSelection);
    host.commit(context.document, "Deleted control point");
    return true;
  }
  if (selectedEdgeIds.length > 0) {
    const nextSelection = host.selectionSnapshot({ selectedVertexId: undefined, selectedEdgeIds: [], selectedHandle: undefined });
    host.history.execute(new DeleteVectorEdgesCommand(selection.primarySelectedNodeId, selectedEdgeIds), context, { afterSelection: nextSelection });
    host.applySelectionSnapshot(nextSelection);
    host.commit(context.document, selectedEdgeIds.length > 1 ? "Deleted edges" : "Deleted edge");
    return true;
  }
  const vertexIds = selectedVertexIds.length > 0 ? selectedVertexIds : selection.selectedVertexId ? [selection.selectedVertexId] : [];
  if (vertexIds.length === 0) return false;
  const nextSelection = host.selectionSnapshot({ selectedVertexId: undefined, selectedVertexIds: [], selectedEdgeIds: [], selectedHandle: undefined });
  const reconnect = options.reconnectVertices ?? false;
  const commands = vertexIds.map(vertexId => new DeleteVectorVertexCommand(selection.primarySelectedNodeId!, vertexId, reconnect));
  host.history.execute(commands.length === 1 ? commands[0]! : new TransactionCommand(reconnect ? "Deleted vertices and reconnected" : "Deleted vertices", commands), context, { afterSelection: nextSelection });
  host.applySelectionSnapshot(nextSelection);
  host.commit(context.document, vertexIds.length > 1
    ? reconnect ? "Deleted vertices and reconnected" : "Deleted vertices"
    : reconnect ? "Deleted vertex and reconnected" : "Deleted vertex");
  return true;
}

export function bendVectorEdge(host: EditorCommandHost, nodeId: string, edgeId: string, handles: { fromHandle: Point; toHandle: Point }): void {
  const edge = vectorNode(nodeId)?.network.edges.find(candidate => candidate.id === edgeId);
  if (!edge) return;
  const context = host.commandContext();
  host.history.execute(new BendVectorEdgeCommand(nodeId, edgeId, {
    fromHandle: edge.from.handle ? { ...edge.from.handle } : undefined, toHandle: edge.to.handle ? { ...edge.to.handle } : undefined,
  }, handles), context, { afterSelection: host.currentSelection() });
  host.commit(context.document, "Bent edge");
}

export function knifeCutEdges(host: EditorCommandHost, nodeId: string, cuts: Array<{ edgeId: string; point: Point; t: number }>): void {
  if (!vectorNode(nodeId) || cuts.length === 0) {
    selectionStore.setActiveTool("select");
    return;
  }
  const commandCuts = cuts.map(cut => ({ ...cut, fromVertexId: createId("vertex"), toVertexId: createId("vertex"), firstEdgeId: createId("edge"), secondEdgeId: createId("edge") }));
  const newVertexIds = commandCuts.flatMap(cut => [cut.fromVertexId, cut.toVertexId]);
  const nextSelection = host.selectionSnapshot({ selectedVertexId: undefined, selectedVertexIds: [], selectedEdgeIds: [], selectedHandle: undefined });
  const context = host.commandContext();
  host.history.execute(new KnifeCutVectorEdgesCommand(nodeId, commandCuts), context, { afterSelection: nextSelection });
  host.applySelectionSnapshot(nextSelection);
  selectionStore.setFreshCutVertexIds(newVertexIds);
  selectionStore.setActiveTool("select");
  host.commit(context.document, cuts.length > 1 ? "Knife cut edges" : "Knife cut edge");
}

export function knifeCutAroundVertex(host: EditorCommandHost, nodeId: string, vertexId: string, edgeIds: string[]): void {
  if (!vectorNode(nodeId) || edgeIds.length === 0) {
    selectionStore.setActiveTool("select");
    return;
  }
  const newVertexId = createId("vertex");
  const nextSelection = host.selectionSnapshot({ selectedVertexId: newVertexId, selectedVertexIds: [newVertexId], selectedEdgeIds: [], selectedHandle: undefined });
  const context = host.commandContext();
  host.history.execute(new KnifeCutAroundVertexCommand(nodeId, vertexId, newVertexId, edgeIds), context, { afterSelection: nextSelection });
  host.applySelectionSnapshot(nextSelection);
  selectionStore.setFreshCutVertexIds([newVertexId]);
  selectionStore.setActiveTool("select");
  host.commit(context.document, edgeIds.length > 1 ? "Knife cut around point" : "Knife split edge from point");
}

export function subdivideSelectedEdges(
  host: EditorCommandHost,
  nodeId: string,
  network: VectorNetwork,
  newVertexIds: string[],
): void {
  if (!vectorNode(nodeId) || newVertexIds.length === 0) {
    return;
  }
  const nextSelection = host.selectionSnapshot({
    selectedNodeIds: [nodeId],
    primarySelectedNodeId: nodeId,
    mode: "vectorEdit",
    selectedVertexId: newVertexIds.at(-1),
    selectedVertexIds: newVertexIds,
    selectedEdgeIds: [],
    selectedHandle: undefined,
  });
  const context = host.commandContext();
  host.history.execute(new SubdivideVectorEdgesCommand(nodeId, network), context, { afterSelection: nextSelection });
  host.applySelectionSnapshot(nextSelection);
  selectionStore.setFreshCutVertexIds(newVertexIds);
  host.commit(context.document, newVertexIds.length > 1 ? "Subdivided edges" : "Subdivided edge");
}

export function extrudeSelectedEdges(
  host: EditorCommandHost,
  nodeId: string,
  network: VectorNetwork,
  newEdgeIds: string[],
  newVertexIds: string[],
): void {
  if (!vectorNode(nodeId) || newEdgeIds.length === 0) {
    return;
  }
  const nextSelection = host.selectionSnapshot({
    selectedNodeIds: [nodeId],
    primarySelectedNodeId: nodeId,
    mode: "vectorEdit",
    selectedVertexId: undefined,
    selectedVertexIds: [],
    selectedEdgeIds: newEdgeIds,
    selectedHandle: undefined,
  });
  const context = host.commandContext();
  host.history.execute(new ExtrudeVectorEdgesCommand(nodeId, network), context, { afterSelection: nextSelection });
  host.applySelectionSnapshot(nextSelection);
  selectionStore.setFreshCutVertexIds(newVertexIds);
  host.commit(context.document, newEdgeIds.length > 1 ? "Extruded edges" : "Extruded edge");
}

export function splitVectorNetworkToRegionObjects(host: EditorCommandHost, nodeId: string): void {
  const selection = host.currentSelection();
  if (
    selection.mode !== "vectorEdit"
    || Math.max(selection.selectedVertexIds?.length ?? 0, selection.selectedVertexId ? 1 : 0) > 0
    || (selection.selectedEdgeIds?.length ?? 0) > 0
    || Boolean(selection.selectedHandle)
  ) {
    return;
  }
  const node = vectorNode(nodeId);
  if (!node) {
    return;
  }
  const parts = splitVectorNetworkToRegions(node.network);
  if (parts.length === 0) {
    return;
  }

  const rotationOrigin = transformRotationOrigin(node.bounds, node.transform);
  let regionIndex = 0;
  let pathIndex = 0;
  const replacements: VectorNetworkNode[] = parts.map(part => {
    const index = part.kind === "closed" ? ++regionIndex : ++pathIndex;
    const suffix = part.kind === "closed" ? `Region ${index}` : `Path ${index}`;
    const bounds = getVectorNetworkBounds(part.network);
    return {
      ...structuredClone(node),
      id: createId("node"),
      name: `${node.name} ${suffix}`,
      bounds,
      transform: node.transform.rotation === 0
        ? structuredClone(node.transform)
        : { ...structuredClone(node.transform), x: rotationOrigin.x, y: rotationOrigin.y },
      network: part.network,
      componentSourceId: undefined,
    };
  });
  const replacementIds = replacements.map(replacement => replacement.id);
  const nextSelection = host.selectionSnapshot({
    selectedNodeIds: replacementIds,
    primarySelectedNodeId: replacementIds.at(-1),
    mode: "select",
    selectedVertexId: undefined,
    selectedVertexIds: [],
    selectedEdgeIds: [],
    selectedHandle: undefined,
  });
  const context = host.commandContext();
  host.history.execute(
    new ReplaceNodesCommand("Split Vector Network to Regions", [nodeId], replacements),
    context,
    { afterSelection: nextSelection },
  );
  host.applySelectionSnapshot(nextSelection);
  selectionStore.setFreshCutVertexIds([]);
  selectionStore.setActiveTool("select");
  host.commit(context.document, replacements.length > 1 ? `Split into ${replacements.length} objects` : "Split into one object");
}

export function toggleVectorRegionFill(host: EditorCommandHost, nodeId: string, edgeIds: string[]): void {
  if (edgeIds.length === 0) return;
  const context = host.commandContext();
  host.history.execute(new ToggleVectorRegionFillCommand(nodeId, edgeIds), context, { afterSelection: host.currentSelection() });
  host.commit(context.document, "Toggled region fill");
}

export function updateVectorVertexSettings(
  host: EditorCommandHost,
  nodeId: string,
  vertexIds: string[],
  patch: ConstructorParameters<typeof UpdateVectorVertexSettingsCommand>[2],
  anchorHandle?: { edgeId: string; endpoint: "from" | "to" },
): void {
  if (vertexIds.length === 0) return;
  const context = host.commandContext();
  const node = vectorNode(nodeId, context.document);
  const handleUpdates = patch.handleMode && patch.handleMode !== "none" && node
    ? vertexIds.flatMap(vertexId => vectorHandleUpdatesForModeChange(node.network, vertexId, patch.handleMode!, anchorHandle && vertexIds.length === 1 ? anchorHandle : undefined))
    : [];
  const commands: Command[] = [new UpdateVectorVertexSettingsCommand(nodeId, vertexIds, patch)];
  if (handleUpdates.length > 0 && node) {
    commands.push(new MoveVectorHandlesCommand(nodeId, handleUpdates.map(update => {
      const current = node.network.edges.find(candidate => candidate.id === update.edgeId)?.[update.endpoint].handle;
      return { edgeId: update.edgeId, endpoint: update.endpoint, handle: current ? { x: current.x, y: current.y } : null };
    }), handleUpdates.map(update => ({
      edgeId: update.edgeId, endpoint: update.endpoint, handle: { x: update.x, y: update.y },
    }))));
  }
  host.history.execute(commands.length === 1 ? commands[0]! : new TransactionCommand("Updated vertex settings", commands), context, { afterSelection: host.currentSelection() });
  host.commit(context.document, "Updated vertex settings");
}
